// Browser input adapter: keyboard, mouse, touch (floating joystick + buttons), Gamepad API -> neutral InputFrame + UI actions.
import { AimMode, emptyInput, InputFrame } from '../core/sim';
import { ActionId, defaultProfile, PAD_START, resolve, Resolved } from './bindings';

export type UiAction = 'pause' | 'confirm' | 'left' | 'right' | 'up' | 'down' | 'back' | 'cycleCamera' | 'cycleFlight' | 'toggleAim' | 'perf' | 'pick1' | 'pick2' | 'pick3' | 'mute' | 'reroll' | 'ban' | 'toggleMap' | 'tabPrev' | 'tabNext';

/** What a bound action does: push a UI action, or latch a one-shot gameplay press (read by frame()). Move is held. */
const ACTION_UI: Partial<Record<ActionId, UiAction>> = {
  pause: 'pause', minimap: 'toggleMap', mute: 'mute', perf: 'perf', camera: 'cycleCamera', aimMode: 'toggleAim',
  confirm: 'confirm', back: 'back', tabPrev: 'tabPrev', tabNext: 'tabNext', reroll: 'reroll', ban: 'ban', pick1: 'pick1', pick2: 'pick2', pick3: 'pick3',
  // owner 2026-11-02: 'on the skill level page, i want to use A/D for left/right' — the movement keys (WASD by default, or
  // whatever they are rebound to) also navigate menus, like the arrows and the D-pad
  moveLeft: 'left', moveRight: 'right', moveUp: 'up', moveDown: 'down',
};
/** Hard-wired keys (never rebindable): Esc pause/back, Enter confirm, arrows navigate (and move). */
const FIXED_KEYS: Record<string, UiAction> = { Escape: 'pause', Enter: 'confirm', NumpadEnter: 'confirm', Space: 'confirm', /* owner 2026-11-02: Space also confirms in menus */ ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
/** A pending "press a button / key" capture for the Controls screen. cb(null) = cancelled (Esc, Start). */
export interface Capture { dev: 'pad' | 'key'; cb: (code: number | string | null) => void }

export class Input {
  private keys = new Set<string>();
  private pressed = new Set<string>();
  private padPrev: boolean[] = [];
  private padAxesPrev = { x: 0, y: 0 };
  ui: UiAction[] = [];
  mouseNdc = { x: 0, y: 0 };
  mouseMoved = false;
  rmb = false;
  aimMode: AimMode = 'auto';
  private lastAim = { x: 0, z: 1 };
  padConnected = false;
  padName = '';
  lastDevice: 'keyboard' | 'gamepad' | 'touch' = 'keyboard';
  // touch joystick state
  private joyId = -1;
  private joyOX = 0; private joyOY = 0;
  joyX = 0; joyY = 0;
  private joyBase: HTMLElement | null = null;
  private joyKnob: HTMLElement | null = null;
  static readonly JOY_R = 32; // stick travel radius (px); full speed arrives well before the rim
  /** Settings › Controls › Touch joystick size: multiplies the travel radius (the CSS base / knob follow --joy-k) */
  joyScale = 1;
  onPadDisconnect?: () => void;
  /** remappable bindings (see bindings.ts); replace with setBindings() */
  bind: Resolved = resolve(defaultProfile());
  /** while set, the next press is handed to cb instead of driving the game or menus */
  capture: Capture | null = null;
  setBindings(r: Resolved): void { this.bind = r; }
  private isHeld(a: ActionId): boolean { for (const [k, acts] of this.bind.key) if (acts.includes(a) && this.keys.has(k)) return true; return false; }

  constructor(canvas: HTMLElement) {
    window.addEventListener('keydown', (e) => {
      // typing in a text box (Settings › Import save code) must not drive menus or the game
      const tg = e.target as HTMLElement | null;
      if (tg && (tg.tagName === 'TEXTAREA' || (tg.tagName === 'INPUT' && (tg as HTMLInputElement).type !== 'checkbox'))) return;
      if (e.repeat) { if (['Space', 'Tab'].includes(e.code)) e.preventDefault(); return; }
      this.lastDevice = 'keyboard';
      if (this.capture) { // Controls screen: this key is the new binding (Esc cancels; validity is checked by the caller)
        e.preventDefault(); const c = this.capture; this.capture = null;
        c.cb(c.dev === 'key' && e.code !== 'Escape' ? e.code : null); return;
      }
      this.keys.add(e.code); this.pressed.add(e.code);
      if (FIXED_KEYS[e.code]) this.ui.push(FIXED_KEYS[e.code]);
      for (const a of this.bind.key.get(e.code) ?? []) { const u = ACTION_UI[a]; if (u) this.ui.push(u); else if (a === 'surge') this.pressed.add('@' + a); }
      if (['Space', 'Tab', 'F3', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
    canvas.addEventListener('mousemove', (e) => {
      const r = canvas.getBoundingClientRect();
      this.mouseNdc.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      this.mouseNdc.y = -((e.clientY - r.top) / r.height) * 2 + 1;
      this.mouseMoved = true;
    });
    canvas.addEventListener('mousedown', (e) => { if (e.button === 2) this.rmb = true; }); // (right mouse no longer fires the Lance: it is always automatic)
    canvas.addEventListener('mouseup', (e) => { if (e.button === 2) this.rmb = false; });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.initTouch(canvas);
    window.addEventListener('gamepadconnected', (e) => { this.padConnected = true; this.padName = (e as GamepadEvent).gamepad.id; });
    window.addEventListener('gamepaddisconnected', () => { this.padConnected = false; this.onPadDisconnect?.(); });
  }

  private initTouch(canvas: HTMLElement): void {
    this.joyBase = document.getElementById('joy');
    this.joyKnob = document.getElementById('joy-knob');
    if (matchMedia('(pointer: coarse)').matches) this.lastDevice = 'touch';
    const start = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      this.lastDevice = 'touch';
      if (this.joyId !== -1) return;
      e.preventDefault();
      this.joyId = e.pointerId; this.joyOX = e.clientX; this.joyOY = e.clientY; this.joyX = 0; this.joyY = 0;
      try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      if (this.joyBase) { this.joyBase.style.left = `${e.clientX}px`; this.joyBase.style.top = `${e.clientY}px`; this.joyBase.classList.add('on'); }
      this.placeKnob(0, 0);
    };
    const move = (e: PointerEvent) => {
      if (e.pointerId !== this.joyId) return;
      e.preventDefault();
      let dx = e.clientX - this.joyOX, dy = e.clientY - this.joyOY;
      const d = Math.hypot(dx, dy), R = Input.JOY_R * this.joyScale;
      // drag the base along if the finger travels past the rim (no dead "wall")
      if (d > R) { this.joyOX += dx * (1 - R / d); this.joyOY += dy * (1 - R / d); dx *= R / d; dy *= R / d;
        if (this.joyBase) { this.joyBase.style.left = `${this.joyOX}px`; this.joyBase.style.top = `${this.joyOY}px`; } }
      const n = Math.hypot(dx, dy) / R;
      // tiny deadzone, full speed at ~60% travel: no straining to reach the rim
      const dead = 0.04, full = 0.6, mag = n < dead ? 0 : Math.min(1, (n - dead) / (full - dead));
      const l = Math.hypot(dx, dy) || 1;
      this.joyX = (dx / l) * mag; this.joyY = (dy / l) * mag;
      this.placeKnob(dx, dy);
    };
    const end = (e: PointerEvent) => {
      if (e.pointerId !== this.joyId) return;
      this.joyId = -1; this.joyX = 0; this.joyY = 0;
      this.joyBase?.classList.remove('on');
    };
    canvas.addEventListener('pointerdown', start, { passive: false });
    canvas.addEventListener('pointermove', move, { passive: false });
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    // on-screen buttons
    // `onRelease`: fire when the finger lifts (still on the button) instead of when it lands. Menu-opening buttons use this:
    // the menu then appears after the touch is over, so its buttons cannot catch the same touch (ghost tap, owner 2026-11-02).
    const btn = (id: string, fn: () => void, onRelease = false) => {
      const el = document.getElementById(id); if (!el) return;
      let armed = -1; // pointer id pressed on this button, release mode only
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault(); e.stopPropagation(); this.lastDevice = e.pointerType === 'mouse' ? this.lastDevice : 'touch'; el.classList.add('down');
        if (onRelease) armed = e.pointerId; else fn();
      });
      const up = () => el.classList.remove('down');
      el.addEventListener('pointerup', (e) => {
        up();
        if (!onRelease || armed !== e.pointerId) return;
        armed = -1;
        const r = el.getBoundingClientRect(); // lifted off the button (dragged away) = cancelled
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) fn();
      });
      el.addEventListener('pointercancel', () => { armed = -1; up(); }); el.addEventListener('pointerleave', () => { armed = -1; up(); });
    };
    btn('t-tf', () => this.pressed.add('@surge'));
    btn('t-pause', () => this.ui.push('pause'), true); // opens the pause menu: on release, not on touch
    btn('t-map', () => this.ui.push('toggleMap')); // HUD icon buttons (top-right, every device)
    btn('t-snd', () => this.ui.push('mute'));
  }

  private placeKnob(dx: number, dy: number): void {
    if (this.joyKnob) this.joyKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }

  /** Release the virtual stick (e.g. when a menu opens). */
  resetTouch(): void { this.joyId = -1; this.joyX = 0; this.joyY = 0; this.joyBase?.classList.remove('on'); }

  private activePad = -1;
  /** Active controller, normalised to the W3C standard layout (see choosePad / normalizePad). */
  private pad(): PadState | null {
    const pads = navigator.getGamepads ? [...navigator.getGamepads()] : [];
    const i = choosePad(pads, this.activePad);
    if (i < 0) return null;
    this.activePad = i;
    return normalizePad(pads[i]!);
  }
  /** the raw active Gamepad (controller vibration, src/app/rumble.ts), or null */
  rawPad(): Gamepad | null {
    if (this.activePad < 0 || !navigator.getGamepads) return null;
    try { return navigator.getGamepads()[this.activePad] ?? null; } catch { return null; }
  }
  /** 'playstation' | 'xbox' | 'generic' for the active controller (drives on-screen button prompts) */
  get padFamily(): PadFamily { return padFamily(this.padName); }

  /** Poll gamepad buttons into UI actions; call once per animation frame. */
  pollUi(): void {
    const p = this.pad();
    if (!p) return;
    this.padConnected = true; this.padName = p.id;
    const b = p.buttons.map((x) => x.pressed);
    const edge = (i: number) => b[i] && !this.padPrev[i];
    if (b.some(Boolean)) this.lastDevice = 'gamepad';
    if (this.capture) { // Controls screen: the next button becomes the binding; Start cancels
      const i = b.findIndex((x, k) => x && !this.padPrev[k]);
      if (i >= 0) { const c = this.capture; this.capture = null; c.cb(c.dev === 'pad' && i !== PAD_START ? i : null); }
      this.padPrev = b; this.padAxesPrev = { x: 0, y: 0 }; return;
    }
    if (edge(PAD_START)) this.ui.push('pause'); // hard-wired: Start always pauses / backs out of menus
    for (let i = 0; i < b.length; i++) {
      if (!edge(i)) continue;
      for (const a of this.bind.pad.get(i) ?? []) { const u = ACTION_UI[a]; if (u) this.ui.push(u); else if (a === 'surge') this.pressed.add('@' + a); }
    }
    if (edge(14)) this.ui.push('left'); // hard-wired: the D-pad always navigates menus
    if (edge(15)) this.ui.push('right');
    if (edge(12)) this.ui.push('up');
    if (edge(13)) this.ui.push('down');
    // stick navigation for menus (the move stick)
    const sw = this.bind.opts.swapSticks ? 2 : 0;
    const ax = p.axes[sw] ?? 0, ay = p.axes[sw + 1] ?? 0;
    if (ax < -0.6 && this.padAxesPrev.x >= -0.6) this.ui.push('left');
    if (ax > 0.6 && this.padAxesPrev.x <= 0.6) this.ui.push('right');
    if (ay < -0.6 && this.padAxesPrev.y >= -0.6) this.ui.push('up');
    if (ay > 0.6 && this.padAxesPrev.y <= 0.6) this.ui.push('down');
    this.padAxesPrev = { x: ax, y: ay };
    this.padPrev = b;
  }

  /** Build a simulation input frame. aimFn converts mouse NDC to a world aim direction. */
  frame(aimFn: (ndcX: number, ndcY: number) => { x: number; z: number } | null): InputFrame {
    const f = emptyInput();
    const k = this.keys;
    let mx = 0, mz = 0;
    if (this.isHeld('moveLeft') || k.has('ArrowLeft')) mx -= 1;
    if (this.isHeld('moveRight') || k.has('ArrowRight')) mx += 1;
    if (this.isHeld('moveUp') || k.has('ArrowUp')) mz -= 1;
    if (this.isHeld('moveDown') || k.has('ArrowDown')) mz += 1;
    const pr = this.pressed;
    f.dash = false; // dash removed; f.ascend stays 0 (the hero is grounded, flight input retired)
    f.beam = false; // Nova Lance is automatic only (owner 2026-11-02)
    f.transform = pr.has('@surge');

    const p = this.pad();
    let padAim = false;
    if (p) {
      const o = this.bind.opts, D = o.deadzone;
      const dz = (v: number) => (Math.abs(v) < D ? 0 : (v - Math.sign(v) * D) / (1 - D));
      const mv = o.swapSticks ? 2 : 0, am = o.swapSticks ? 0 : 2;
      const lx = dz(p.axes[mv] ?? 0), ly = dz(p.axes[mv + 1] ?? 0);
      if (lx || ly) { mx += lx; mz += ly; this.lastDevice = 'gamepad'; }
      const rx = dz(p.axes[am] ?? 0), ry = dz(p.axes[am + 1] ?? 0) * (o.invertAimY ? -1 : 1);
      if (Math.hypot(rx, ry) > 0.35) { f.aimX = rx; f.aimZ = ry; padAim = true; }
    }
    mx += this.joyX; mz += this.joyY;
    f.moveX = mx; f.moveZ = mz;
    const ml = Math.hypot(mx, mz);
    if (ml > 0.2) { this.lastAim.x = mx / ml; this.lastAim.z = mz / ml; }
    // Aim modes: auto = never manual; assist = auto unless R-stick / right mouse steers;
    // manual = always aim (R-stick, else mouse, else last movement direction).
    const mouseAim = () => { const a = aimFn(this.mouseNdc.x, this.mouseNdc.y); if (a) { f.aimX = a.x; f.aimZ = a.z; f.manualAim = true; } };
    if (this.aimMode === 'auto') { f.aimX = 0; f.aimZ = 0; f.manualAim = false; }
    else if (this.aimMode === 'assist') {
      if (padAim) f.manualAim = true;
      else if (this.rmb && this.lastDevice === 'keyboard') mouseAim();
    } else {
      if (padAim) f.manualAim = true;
      else if (this.lastDevice === 'keyboard' && this.mouseMoved) mouseAim();
      if (!f.manualAim) { f.aimX = this.lastAim.x; f.aimZ = this.lastAim.z; f.manualAim = true; }
      else { const l = Math.hypot(f.aimX, f.aimZ) || 1; this.lastAim.x = f.aimX / l; this.lastAim.z = f.aimZ / l; }
    }
    return f;
  }

  consumePresses(): void { this.pressed.clear(); }
  takeUi(): UiAction[] { const u = this.ui; this.ui = []; return u; }
}


// ---------------- controller support (PS5 DualSense, DualShock 4, Xbox 360/One/Series, DS4Windows virtual pads) ----------------
export type PadFamily = 'playstation' | 'xbox' | 'generic';
export interface PadState { id: string; mapping: string; buttons: { pressed: boolean; value: number }[]; axes: number[] }

const active = (p: Gamepad) => p.buttons.some((b) => b.pressed || b.value > 0.3) || p.axes.some((a) => Math.abs(a) > 0.35);

/** Pick which connected controller drives the game.
 *  DS4Windows exposes the physical DualSense AND a virtual Xbox 360 pad; Chrome may list both. Prefer pads reporting the
 *  W3C 'standard' mapping, follow whichever one is actually being used, and otherwise keep the previous choice. */
export function choosePad(pads: (Gamepad | null)[], prev: number): number {
  const ok = pads.map((p, i) => (p && p.connected ? i : -1)).filter((i) => i >= 0);
  if (!ok.length) return -1;
  const std = ok.filter((i) => pads[i]!.mapping === 'standard');
  const pool = std.length ? std : ok;
  const moving = pool.find((i) => active(pads[i]!));
  if (moving !== undefined) return moving;
  if (pool.includes(prev)) return prev;
  return pool[0];
}

export function padFamily(id: string): PadFamily {
  if (/054c|dualsense|dualshock|wireless controller|playstation|ps[345]/i.test(id)) return 'playstation';
  if (/xbox|xinput|045e/i.test(id)) return 'xbox';
  return 'generic';
}

/** Return the pad in standard layout. Raw (non-standard) Sony HID layouts are remapped:
 *  raw □ ✕ ○ △ = 0 1 2 3 -> standard ✕ ○ □ △ = 0 1 2 3; right stick on raw axes 2/5 -> standard 2/3. */
export function normalizePad(p: Gamepad): PadState {
  const buttons = p.buttons.map((b) => ({ pressed: b.pressed, value: b.value }));
  if (p.mapping === 'standard' || padFamily(p.id) !== 'playstation') return { id: p.id, mapping: p.mapping, buttons, axes: [...p.axes] };
  const B = (i: number) => buttons[i] ?? { pressed: false, value: 0 };
  const std = [B(1), B(2), B(0), B(3), B(4), B(5), B(6), B(7), B(8), B(9), B(10), B(11)];
  const ax = p.axes;
  // raw d-pad usually arrives as a hat axis (axes[9]) — decode to standard buttons 12-15
  // HID hat: -1 up, then clockwise in steps of 2/7 to +1 up-left; ~3.29 when centred
  const hat = ax[9] ?? 3.3, dir = Math.abs(hat) <= 1.05 ? Math.round(((hat + 1) * 7) / 2) : -1;
  const up = dir === 7 || dir === 0 || dir === 1, right = dir >= 1 && dir <= 3, down = dir >= 3 && dir <= 5, left = dir >= 5 && dir <= 7;
  std.push({ pressed: up, value: +up }, { pressed: down, value: +down }, { pressed: left, value: +left }, { pressed: right, value: +right }, B(12));
  return { id: p.id, mapping: 'standard*', buttons: std, axes: [ax[0] ?? 0, ax[1] ?? 0, ax[2] ?? 0, ax[5] ?? ax[3] ?? 0] };
}

/** Button prompt glyphs per controller family (standard indices: 0 south, 1 east, 2 west, 3 north). */
export const GLYPHS: Record<PadFamily, { south: string; east: string; west: string; north: string; start: string; select: string; r3: string; lb: string; rb: string }> = {
  playstation: { south: '✕', east: '○', west: '□', north: '△', start: 'OPTIONS', select: 'CREATE', r3: 'R3', lb: 'L1', rb: 'R1' },
  xbox: { south: 'A', east: 'B', west: 'X', north: 'Y', start: 'Menu', select: 'View', r3: 'RS', lb: 'LB', rb: 'RB' },
  generic: { south: 'A', east: 'B', west: 'X', north: 'Y', start: 'Start', select: 'Select', r3: 'R3', lb: 'LB', rb: 'RB' },
};
